import React, { useContext, useState, useEffect } from 'react';
import useGetFeatureAccess from 'hooks/useGetUserAuthData';
import {
    AccessLevels, IFeatureAccess, IFeatureAccessMap, TAccessLevel, TFeatureId, TUserClassId, IRole,
} from 'types/auth';
import { IAuthenticatedUser, User } from 'queries/UserMenu/useGetAuthenticatedUser';
import { USER_CLASSES, getUserSpecificSelectedOrgKey } from 'services/constants';
import useGetUserOrganizations, { IUserOrganization } from 'hooks/useGetUserOrganizations';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';
import { getCookie, setCrossDomainCookie } from 'helpers/localStorage/cookies';
import { initializePendo, updatePendoAccount } from 'helpers/pendo';
import { initializeHubble } from 'utils/initHubble';
import { setDatadogRumUser } from 'utils/initDatadogRum';

export interface IAuthContext {
    /**
     * selectedOrganizationId: correlation ID of the selected organization.
     */
    selectedOrganizationId?: string;

    /**
     * handleUpdateSelectedOrganization: function to update the selected organization by int ID.
     * @param orgId number - The int ID of the organization to select.
     */
    handleUpdateSelectedOrganization: (orgId: number) => void;
    userOrganizations: IUserOrganization[];
    isLoadingUserOrganizations: boolean;
    orgPermissions: IFeatureAccessMap;
    isLoadingAuthData: boolean;
    isFeatureAccessibleToUser: (featureIds: TFeatureId[], requiredAccessLevel: TAccessLevel) => boolean;
    role: IRole | null;
    user: User | null;
    hasDevelopmentAccess: boolean;
    isFirstDealLogin: boolean;
    exampleDealId: number | null;
}

const defaultAuthContext: IAuthContext = {
    selectedOrganizationId: undefined,
    handleUpdateSelectedOrganization: () => { },
    userOrganizations: [],
    isLoadingUserOrganizations: true,
    orgPermissions: {},
    isLoadingAuthData: true,
    isFeatureAccessibleToUser: () => false,
    role: null,
    user: null,
    hasDevelopmentAccess: false,
    isFirstDealLogin: false,
    exampleDealId: null,
};

export const AuthContext = React.createContext<IAuthContext>(defaultAuthContext);

export const useAuthContext = () => useContext(AuthContext);
export default useAuthContext;

export const featureArrayToObject = (features: IFeatureAccess[] | undefined): IFeatureAccessMap => {
    if (!features) return {};
    return features.reduce((featuresObj, feature) => ({
        ...featuresObj,
        [feature.id]: feature,
    }), {} as IFeatureAccessMap);
};

export const isFeatureAccessible = (
    featureIds: number[],
    requiredAccessLevel: TAccessLevel,
    orgPermissions: IFeatureAccessMap,
    userClass: TUserClassId | undefined,
) => {
    if (userClass === USER_CLASSES.SUPER_ADMIN) return true;
    return featureIds.some(featureId => {
        const accessLevel = orgPermissions?.[featureId]?.accessLevel ?? AccessLevels.NO_ACCESS;
        return accessLevel >= requiredAccessLevel;
    });
};

export const AuthProvider = ({
    children,
    authenticatedUser,
}: {
    children: React.ReactNode;
    authenticatedUser: IAuthenticatedUser
}) => {
    const userSpecificSelectedOrgKey = getUserSpecificSelectedOrgKey(authenticatedUser.user.id);

    const { data: userOrganizations = [], isLoading, isPlaceholderData } = useGetUserOrganizations();
    const isLoadingUserOrganizations = isLoading || isPlaceholderData;

    const getCorrelationIdFromId = (orgId: number | undefined): string => {
        if (orgId === authenticatedUser.primaryOrganization.organizationId) {
            return authenticatedUser.primaryOrganization.organizationCorrelationId;
        }
        const match = orgId ? authenticatedUser.secondaryOrganizations.find(org => orgId === org.organizationId) : undefined;
        if (match) {
            return match.organizationCorrelationId;
        }

        // For super admin users, allow switching to any organization in the system
        if (authenticatedUser.user.userClass === USER_CLASSES.SUPER_ADMIN && orgId) {
            const superAdminMatch = userOrganizations.find(userOrg => userOrg.organization.id === orgId);
            if (superAdminMatch) {
                return superAdminMatch.organization.correlationId;
            }
        }

        // if the org ID from the cookie is not provided, or if it does not match any of the
        // user's organizations, default to the primary organization and update the cookie
        const primaryOrg = authenticatedUser.primaryOrganization;
        setCrossDomainCookie(userSpecificSelectedOrgKey, String(primaryOrg.organizationId), 34560000);
        return primaryOrg.organizationCorrelationId;
    };

    const getInitialSelectedOrganizationId = () => getCorrelationIdFromId(Number(getCookie(userSpecificSelectedOrgKey)));

    const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>(getInitialSelectedOrganizationId);

    const handleUpdateSelectedOrganization = (orgId: number) => {
        setCrossDomainCookie(userSpecificSelectedOrgKey, String(orgId), 34560000);
        setSelectedOrganizationId(getCorrelationIdFromId(orgId));
    };

    const { data: userAuthData, isLoading: isLoadingUserAuthData } = useGetFeatureAccess(selectedOrganizationId!, {
        enabled: Boolean(selectedOrganizationId),
        staleTime: Infinity,
    });

    const orgPermissions = featureArrayToObject(userAuthData?.featureAccess) || {};

    const { setIdentity } = useTrackingCode();

    const isFeatureAccessibleToUser = (
        featureIds: TFeatureId[], requiredAccessLevel: TAccessLevel,
    ) => isFeatureAccessible(featureIds, requiredAccessLevel, orgPermissions, authenticatedUser.user.userClass);

    useEffect(() => {
        if (
            !isLoadingUserOrganizations
            && authenticatedUser
        ) {
            setIdentity(authenticatedUser.user.email, { id: authenticatedUser.user.id });
            if ((window as any).heap) {
                (window as any).heap.identify(authenticatedUser.user.email);
                (window as any).heap.addUserProperties({
                    email: authenticatedUser.user.email,
                    name: authenticatedUser.user.name,
                    user_class: authenticatedUser.user.userClass,
                    organization_id: userOrganizations.map(org => org.organization.id).join(', '),
                    organization_name: userOrganizations.map(org => org.organization.name).join(', '),
                });
            }

            // Initialize Pendo with user and organization data
            const primaryOrg = authenticatedUser.primaryOrganization;
            initializePendo(
                {
                    id: String(authenticatedUser.user.id),
                    email: authenticatedUser.user.email,
                    full_name: authenticatedUser.user.name,
                },
                {
                    id: primaryOrg.organizationCorrelationId,
                    name: primaryOrg.organizationName,
                },
            );

            // Initialize Hubble for contextual UX surveys
            initializeHubble(String(authenticatedUser.user.id), authenticatedUser.user.email);

            setDatadogRumUser(String(authenticatedUser.user.id));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authenticatedUser, userOrganizations, isLoadingUserOrganizations]);

    // Update Pendo when organization changes
    useEffect(() => {
        if (selectedOrganizationId && userOrganizations.length > 0) {
            const selectedOrg = userOrganizations.find(
                org => org.organization.correlationId === selectedOrganizationId,
            );
            if (selectedOrg) {
                updatePendoAccount(
                    selectedOrg.organization.correlationId,
                    selectedOrg.organization.name,
                );
            }
        }
    }, [selectedOrganizationId, userOrganizations]);

    return (
        <AuthContext.Provider
            value={{
                selectedOrganizationId: selectedOrganizationId || undefined,
                handleUpdateSelectedOrganization,
                userOrganizations,
                isLoadingUserOrganizations,
                orgPermissions,
                isLoadingAuthData: isLoadingUserAuthData,
                isFeatureAccessibleToUser,
                role: userAuthData?.role || null,
                user: authenticatedUser.user || null,
                hasDevelopmentAccess: userAuthData?.hasDevelopmentAccess ?? false,
                isFirstDealLogin: authenticatedUser.isFirstDealLogin ?? false,
                exampleDealId: authenticatedUser.exampleDealId ?? null,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};
